import type { z } from 'zod';
import {
  analyzeTrendsToolInputSchema,
  archiveDatasetSchema,
  compareEngagementToolInputSchema,
  getArchiveDataToolInputSchema,
  getImportStatusToolInputSchema,
  getProfileHighlightsToolInputSchema,
  getTweetToolInputSchema,
  getUserProfileToolInputSchema,
  searchTimelineToolInputSchema,
  setDailyDigestPreferenceToolInputSchema,
  setPulsePreferenceToolInputSchema,
} from '@/lib/chat/tools/input-schemas';
import { getRecordValue } from '@/lib/utils/type-guards';

const toolArgumentSchemas = {
  searchTimeline: searchTimelineToolInputSchema,
  getTweet: getTweetToolInputSchema,
  analyzeTrends: analyzeTrendsToolInputSchema,
  compareEngagement: compareEngagementToolInputSchema,
  getUserProfile: getUserProfileToolInputSchema,
  getProfileHighlights: getProfileHighlightsToolInputSchema,
  getArchiveData: getArchiveDataToolInputSchema,
  getImportStatus: getImportStatusToolInputSchema,
  setDailyDigestPreference: setDailyDigestPreferenceToolInputSchema,
  setPulsePreference: setPulsePreferenceToolInputSchema,
} as const;

type ToolArgumentSchemas = typeof toolArgumentSchemas;

export type KnownToolName = keyof ToolArgumentSchemas;

type InferToolArguments<TSchema> = TSchema extends z.ZodTypeAny ? z.infer<TSchema> : never;

export type ToolArguments<TName extends KnownToolName> = InferToolArguments<ToolArgumentSchemas[TName]>;

export type ArchiveDataset = z.infer<typeof archiveDatasetSchema>;

export const knownToolNames = Object.keys(toolArgumentSchemas).filter((name): name is KnownToolName =>
  Object.hasOwn(toolArgumentSchemas, name)
);

export const isKnownToolName = (candidate: string): candidate is KnownToolName =>
  Object.hasOwn(toolArgumentSchemas, candidate);

export function parseToolArguments<TName extends KnownToolName>(
  name: TName,
  args: Record<string, unknown> | undefined
): ToolArguments<TName> | null {
  const schema = toolArgumentSchemas[name] as z.ZodTypeAny;
  const parsed = schema.safeParse(args ?? undefined);
  if (!parsed.success || parsed.data === undefined) {
    return null;
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return parsed.data as ToolArguments<TName>;
}

export interface ToolArgumentSummary {
  dataset?: ArchiveDataset;
  query?: string;
  tweetId?: string;
  username?: string;
}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const readDataset = (value: unknown): ArchiveDataset | undefined => {
  const parsed = archiveDatasetSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
};

const assignKnownArguments = (
  summary: ToolArgumentSummary,
  toolName: KnownToolName,
  args: Record<string, unknown> | undefined
): ToolArgumentSummary => {
  switch (toolName) {
    case 'searchTimeline': {
      const parsed = parseToolArguments('searchTimeline', args);
      if (!parsed) {
        return summary;
      }
      const queryValue = readString(parsed.query);
      if (queryValue !== undefined) {
        summary.query = queryValue;
      }
      break;
    }
    case 'getTweet': {
      const parsed = parseToolArguments('getTweet', args);
      if (!parsed) {
        return summary;
      }
      const tweetId = readString(parsed.tweetId);
      if (tweetId !== undefined) {
        summary.tweetId = tweetId;
      }
      break;
    }
    case 'getUserProfile': {
      const parsed = parseToolArguments('getUserProfile', args);
      if (!parsed) {
        return summary;
      }
      const username = readString(parsed.username);
      if (username !== undefined) {
        summary.username = username;
      }
      break;
    }
    case 'getArchiveData': {
      const parsed = parseToolArguments('getArchiveData', args);
      if (!parsed) {
        return summary;
      }
      const dataset = readDataset(parsed.dataset);
      if (dataset !== undefined) {
        summary.dataset = dataset;
      }
      break;
    }
    case 'analyzeTrends':
    case 'compareEngagement':
    case 'getProfileHighlights':
    case 'getImportStatus':
    case 'setDailyDigestPreference':
    case 'setPulsePreference': {
      break;
    }
    default: {
      break;
    }
  }

  return summary;
};

const assignFallbackArguments = (summary: ToolArgumentSummary, args?: Record<string, unknown>): ToolArgumentSummary => {
  if (!args) {
    return summary;
  }

  const queryValue = readString(getRecordValue(args, 'query'));
  if (queryValue !== undefined) {
    summary.query = queryValue;
  }
  const usernameValue = readString(getRecordValue(args, 'username'));
  if (usernameValue !== undefined) {
    summary.username = usernameValue;
  }
  const tweetIdValue = readString(getRecordValue(args, 'tweetId'));
  if (tweetIdValue !== undefined) {
    summary.tweetId = tweetIdValue;
  }
  const datasetValue = readDataset(getRecordValue(args, 'dataset'));
  if (datasetValue !== undefined) {
    summary.dataset = datasetValue;
  }

  return summary;
};

export function buildToolArgumentSummary(
  toolName: string,
  args: Record<string, unknown> | undefined
): ToolArgumentSummary {
  const summary: ToolArgumentSummary = {};
  if (isKnownToolName(toolName)) {
    assignKnownArguments(summary, toolName, args);
    const hasAllPrimaryFields = Boolean(summary.query && summary.username && summary.tweetId && summary.dataset);
    if (!hasAllPrimaryFields) {
      assignFallbackArguments(summary, args);
    }
    return summary;
  }
  return assignFallbackArguments(summary, args);
}
